import React, { useRef } from "react";
import Image from "next/image";
import { useMutation } from "@apollo/client";
import { Icon } from "@/components/icon";
import css from "./.module.scss";
import { useFavListingsState } from "@/contexts/fav-listings/favListings.provider";
import { usePath } from "@/contexts/path/path";
import {
  addImgReportedID,
  CloudfrontImageLoader,
  financialFormat,
  getSpecs,
  getStoredImgsReportedIDs,
} from "@/lib/helpers";
import { REPORT_IMAGES_ISSUE } from "@/graphql/mutations/report";
import { useAuthState } from "@/contexts/auth/auth.provider";
import NativeListingCard from "./native";
import DailyListingCard from "@/components/Listing/ListingCard/daily";
import AppLink from "@/components/new-resolver/app.ink";
import { RENT_CATEGORIES } from "@/components/Listing/ListingScreen";
import { RENT_PERIOD_MAP } from "@/components/Listing/ListingScreen/alert";
import { useAppState } from "@/contexts/app/app.provider";

export function getPrice(listing, filters) {
  const {
    price: p,
    price_12_payments,
    price_4_payments,
    price_2_payments,
    is_auction,
    rega_total_price,
  } = listing || {};

  if (!!rega_total_price && p !== rega_total_price) {
    // const discountPercentage = Math.round(
    //   ((rega_total_price - p) / rega_total_price) * 100,
    // );

    return (
      <div
        style={{
          display: "flex",
          gap: "8px",
          alignItems: "flex-start",
        }}
      >
        <p className={css.price}>{financialFormat(p, "ريال")}</p>
        {/*<span className={css.discountChip}>خصم {discountPercentage}%</span>*/}
      </div>
    );
  }

  if (is_auction) {
    return (
      <span className={css.auction}>
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.04024 12.0766H7.70628L7.67673 11.9956C7.43584 11.3501 6.81817 10.9227 6.12661 10.9242H2.62738C1.93729 10.9242 1.31961 11.3516 1.07875 11.9971L1.04771 12.0781H0.701932C0.314773 12.0781 2.07811e-05 12.392 2.07811e-05 12.7781V13.207C-0.00293755 13.5931 0.310336 13.9085 0.697495 13.9099H0.701932L8.04024 13.9055C8.42738 13.9055 8.74215 13.5916 8.74215 13.2055V12.7767C8.74215 12.3905 8.42738 12.0766 8.04024 12.0766ZM13.555 11.3383L9.13669 6.60772C9.59331 6.18919 10.0041 5.72202 10.3602 5.21507C10.6735 5.35359 11.037 5.31233 11.3119 5.10748C11.7316 4.79506 11.8173 4.20116 11.504 3.78262L8.94902 0.379831C8.74363 0.104248 8.40523 -0.0372218 8.06387 0.00846275C7.54371 0.0762475 7.17872 0.552261 7.24817 1.071C5.51186 1.65164 4.00905 2.77314 2.95987 4.26894C2.64512 4.13042 2.2816 4.17168 2.00822 4.37653C1.58708 4.68896 1.50137 5.28286 1.81612 5.70139L4.36812 9.10418C4.57502 9.38271 4.91782 9.52566 5.26214 9.4785C5.78082 9.40628 6.14285 8.9288 6.07192 8.41007C6.65857 8.21406 7.22454 7.95321 7.75503 7.63489L11.0577 13.2026C11.1242 13.319 11.2055 13.428 11.3001 13.5238C11.9044 14.1457 12.9004 14.1605 13.5225 13.5562C14.1461 12.952 14.1609 11.9602 13.555 11.3383Z"
            fill="#008236"
          />
        </svg>
        <span>مزاد</span>
      </span>
    );
  }

  let price = financialFormat(
    p,
    `${listing.category}` === "1"
      ? "ريال/" +
          RENT_PERIOD_MAP[
            listing.rent_period || filters?.rent_period?.eq || "3"
          ]
      : "ريال",
  );

  if (
    RENT_CATEGORIES[listing.category] &&
    (price_4_payments || price_2_payments || price_12_payments)
  )
    price = financialFormat(p, "ريال/سنوي");

  if (filters?.price_12_payments && price_12_payments)
    price = financialFormat(Math.round(price_12_payments / 12), "ريال/شهري");
  if (filters?.price_4_payments && price_4_payments)
    price = financialFormat(price_4_payments, "ريال/ربع سنوي");
  if (filters?.price_2_payments && price_2_payments)
    price = financialFormat(price_2_payments, "ريال/نصف سنوي");

  return price;
}
export const getImageSrc = (images) =>
  Array.isArray(images) && images[0] ? images[0] : "";

export default function ListingCard({
  onClick,
  listing,
  preloadImage,
  index,
  imageClass,
  trackClickEvent,
  horizontal,
  isRnplCard,
}) {
  const {
    id,
    title,
    published,
    district,
    content,
    city,
    special,
    sov_campaign_id,
    status,
    licensing_request,
    pending_payment,
  } = listing || {};
  const { updateContext, filters, basePath, categoriesDictionary } = usePath();

  const token = useAuthState("token");

  const { isMobile } = useAppState();
  const price = getPrice(listing, filters);
  const [reportImagesIssue] = useMutation(REPORT_IMAGES_ISSUE, {
    skip: !token,
    context: { token },
  });
  const image = getImageSrc(listing.imgs);
  const imageWrapper = useRef();

  const isFavorite = useFavListingsState(listing.id);
  const specs = getSpecs(listing);
  const isLicenseExpired = status === 10;
  const isPreciseLocationApproved =
    listing?.precise_location_request_status === "approved";
  return (
    <div className={css.link}>
      <AppLink
        onClick={(e) => {
          if ((basePath || "").includes("map")) {
            e.stopPropagation();
            e.preventDefault();
          }
          if (onClick) return onClick(e, index);
          if (!listing.path) return;
          trackClickEvent && trackClickEvent();
          return updateContext({
            currentFrozenIndex: index,
            listing: {
              ...listing,
              loadedImage: imageWrapper.current?.firstChild?.firstChild
                ?.complete
                ? imageWrapper.current.firstChild?.firstChild.currentSrc
                : undefined,
            },
          });
        }}
        path={listing.path || `/ad/${listing.id}`}
        scroll
      >
        <div
          className={css.listingCard + " " + (horizontal ? css.horizontal : "")}
        >
          <div className={css.absolute}>
            {(special || sov_campaign_id) && (
              <div
                className={
                  css.sov /*sov_campaign_id ? css.sov : css.specialText*/
                }
              >
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
                    />
                  </svg>
                </span>
                <p> مميز</p>
              </div>
            )}

            {isFavorite && (
              <button type="button" className={css.clickHandler}>
                <Icon
                  name="BorderHeart-colored"
                  alt="مفضلة"
                  width={25}
                  height={25}
                />
              </button>
            )}
          </div>

          <div className={css.content}>
            {horizontal ? null : (
              <div className={css.titleRow}>
                <h4 className={isPreciseLocationApproved && css.inline}>
                  {isPreciseLocationApproved && (
                    <svg
                      width="12"
                      height="14"
                      viewBox="0 0 12 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 5.94049C12 6.48383 11.8906 7.05433 11.6719 7.652C11.4531 8.24968 11.1344 8.8715 10.7156 9.51746C10.2969 10.1634 9.775 10.8305 9.15 11.5188C8.525 12.207 7.80625 12.9133 6.99375 13.6378C6.85625 13.7585 6.7 13.8491 6.525 13.9094C6.35 13.9698 6.175 14 6 14C5.825 14 5.65 13.9698 5.475 13.9094C5.3 13.8491 5.14375 13.7585 5.00625 13.6378C4.19375 12.9133 3.475 12.207 2.85 11.5188C2.225 10.8305 1.70312 10.1634 1.28437 9.51746C0.865625 8.8715 0.546875 8.24968 0.328125 7.652C0.109375 7.05433 0 6.48383 0 5.94049C0 4.12937 0.603125 2.6865 1.80938 1.6119C3.01562 0.537301 4.4125 0 6 0C7.5875 0 8.98438 0.537301 10.1906 1.6119C11.3969 2.6865 12 4.12937 12 5.94049Z"
                        fill="#52B44A"
                      />
                      <path
                        d="M5.07194 6.80269L4.46763 6.17489C4.29496 5.99552 4.09353 5.90583 3.86331 5.90583C3.63309 5.90583 3.43165 5.99552 3.25899 6.17489C3.08633 6.35426 3 6.56726 3 6.8139C3 7.06054 3.08633 7.27354 3.25899 7.45291L4.46763 8.73094C4.64029 8.91031 4.84173 9 5.07194 9C5.30216 9 5.5036 8.91031 5.67626 8.73094L8.74101 5.54709C8.91367 5.36771 9 5.15471 9 4.90807C9 4.66144 8.91367 4.44843 8.74101 4.26906C8.56834 4.08969 8.36331 4 8.1259 4C7.88849 4 7.68345 4.08969 7.51079 4.26906L5.07194 6.80269Z"
                        fill="white"
                      />
                    </svg>
                  )}
                  {listing.title ||
                    listing.unit_code ||
                    categoriesDictionary?.[listing.category]?.name}
                </h4>
                {isLicenseExpired && (
                  <span className={css.licenseExpired}>ترخيص منتهي</span>
                )}
              </div>
            )}

            <div className={css.price}>
              {isRnplCard
                ? `تبدأ من ${financialFormat(
                    listing?.rnpl_monthly_price,
                  )} ريال / شهري`
                : published
                  ? price
                  : pending_payment
                    ? "بانتظار الدفع"
                    : licensing_request
                      ? "جاري الترخيص"
                      : "طلب تسويق"}
            </div>

            {!!specs?.length && (
              <div className={css.specs}>
                {specs.map(({ icon, value, alt }, index) => (
                  <div className={css.spec} key={`${icon}-${value}-${index}`}>
                    {icon && (
                      <Icon
                        name={icon}
                        alt={alt}
                        width={20}
                        height={20}
                        priority
                      />
                    )}
                    {value}
                  </div>
                ))}
              </div>
            )}

            {horizontal ? (
              <div className={css.description}>
                <p>
                  <small>{listing.title}</small>
                </p>
              </div>
            ) : (
              <>
                {content && (
                  <div className={css.description}>
                    <p>{published ? content : "تفاصيل الإعلان مخفية"}</p>
                  </div>
                )}
              </>
            )}
            {horizontal ? null : (
              <div className={css.footer}>
                <p>
                  <span>{city}</span>
                  <span>{district ? `-${district}` : ""}</span>
                </p>
              </div>
            )}
          </div>

          <div
            ref={imageWrapper}
            className={css.imageWrapper + " " + imageClass}
          >
            <Image
              loader={CloudfrontImageLoader}
              src={image || "villa-default.png"}
              onError={() => {
                const reportedImgs = getStoredImgsReportedIDs();
                if (!(reportedImgs && reportedImgs[image]) && token) {
                  addImgReportedID(image);
                  reportImagesIssue({
                    variables: {
                      listing_id: id,
                      images: image,
                    },
                  });
                }
              }}
              style={{
                objectFit: "cover",
              }}
              fill
              alt={title || ""}
              sizes="(max-width: 640px) 100px,(max-width: 1024px) 640px,750px"
              priority={preloadImage}
            />
          </div>
        </div>
      </AppLink>
    </div>
  );
}

function getCategoryFromTitle(title) {
  if (!title) return "";
  const parts = title.split(" في ");
  return parts[0] ? parts[0].trim() : "";
}

export function CompactListingCard({ listing, isRnplCard }) {
  const token = useAuthState("token");
  const price = financialFormat(listing?.price, "ريال");
  const { isMobile } = useAppState();

  const specs = getSpecs(listing);
  const image = getImageSrc(listing?.imgs);
  const title = getCategoryFromTitle(listing?.title);

  const [reportImagesIssue] = useMutation(REPORT_IMAGES_ISSUE, {
    skip: !token,
    context: { token },
  });

  return (
    <div className={css.compactListingCard}>
      <div className={css.details}>
        <p className={css.title}>{title}</p>
        <p className={css.price}>
          {isRnplCard
            ? `تبدأ من ${financialFormat(
                listing?.rnpl_monthly_price,
              )} ريال / شهري`
            : price}
        </p>
        <div className={css.specs}>
          {!!specs?.length &&
            specs.map(({ icon, value, alt }, index) => (
              <div className={css.spec} key={`${icon}-${value}-${index}`}>
                {icon && (
                  <Icon name={icon} alt={alt} width={20} height={20} priority />
                )}
                {value}
              </div>
            ))}
        </div>
        <p className={css.address}>{listing?.address}</p>
      </div>
      <div className={css.imageWrapper}>
        <Image
          loader={CloudfrontImageLoader}
          src={image || "villa-default.png"}
          onError={() => {
            const reportedImgs = getStoredImgsReportedIDs();
            if (!(reportedImgs && reportedImgs[image]) && token) {
              addImgReportedID(image);
              reportImagesIssue({
                variables: {
                  listing_id: listing.id,
                  images: image,
                },
              });
            }
          }}
          style={{
            objectFit: "cover",
          }}
          fill
          alt={"property image"}
          sizes={
            isMobile
              ? "350px"
              : `(max-width: 640px) 350px,
               (max-width: 1024px) 640px,
               1080px`
          }
        />
      </div>
    </div>
  );
}

export function Listing({
  onClick,
  listing,
  index,
  preloadImage,
  imageClass,
  trackClickEvent,
  isRnplCard,
}) {
  const pre = typeof preloadImage == "undefined" ? index < 4 : preloadImage;

  if (!listing) return null;
  if (listing.native)
    return <NativeListingCard listing={listing} preloadImage={pre} />;

  if (listing.daily_rentable)
    return (
      <DailyListingCard
        listing={listing}
        onClick={onClick}
        index={index}
        imageClass={imageClass}
        preloadImage={pre}
        trackClickEvent={trackClickEvent}
      />
    );
  return (
    <ListingCard
      listing={listing}
      onClick={onClick}
      index={index}
      imageClass={imageClass}
      preloadImage={pre}
      trackClickEvent={trackClickEvent}
      isRnplCard={isRnplCard}
    />
  );
}

export function HorizontalListing({
  onClick,
  listing,
  index,
  preloadImage,
  imageClass,
  trackClickEvent,
}) {
  const pre = typeof preloadImage == "undefined" ? index < 4 : preloadImage;

  if (!listing) return null;
  if (listing.native)
    return <NativeListingCard listing={listing} preloadImage={pre} />;

  if (listing.daily_rentable)
    return (
      <DailyListingCard
        listing={listing}
        onClick={onClick}
        horizontal
        index={index}
        imageClass={imageClass}
        preloadImage={pre}
        trackClickEvent={trackClickEvent}
      />
    );
  return (
    <ListingCard
      listing={listing}
      onClick={onClick}
      horizontal
      index={index}
      imageClass={imageClass}
      preloadImage={pre}
      trackClickEvent={trackClickEvent}
    />
  );
}
export { DailyListingCard } from "@/components/Listing/ListingCard/daily";
